import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { readableDate } from "common/dates";
import DefaultLoader from "components/common/DefaultLoader";
import DocumentInfoDialog from "components/layers/DocumentInfoDialog";
import FileDetail from "components/templates/FileDetail";
import * as fileUtils from "fileUtils";
import { useState } from "react";
import { BsDownload as DownloadIcon } from "react-icons/bs";
import * as docGenService from "services/docGenService";
import * as generatedDocumentsService from "services/generatedDocumentsService";

const ActionMenu = ({ document, submissionId, layerId, layer }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [
    deleteDocument,
  ] = generatedDocumentsService.useDeleteGeneratedDocumentMutation();
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const handleInfo = (event) => {
    event.stopPropagation();
    setInfoDialogOpen(true);
    handleClose();
  };

  const handleDelete = async (event) => {
    event.stopPropagation();
    await deleteDocument({
      submissionId,
      layerId,
      documentId: document.id,
    });
    handleClose();
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size={"small"}
        sx={{ color: "text.secondary" }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleInfo}>{"Info"}</MenuItem>
        <MenuItem onClick={handleDelete}>{"Delete"}</MenuItem>
      </Menu>
      <DocumentInfoDialog
        document={document}
        layer={layer}
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
      />
    </>
  );
};

const DownloadButton = ({ document }) => {
  const theme = useTheme();
  const [isDownloading, setIsDownloading] = useState(false);
  const [
    downloadGeneratedDocumentFile,
  ] = docGenService.useDownloadGeneratedDocumentFileMutation();

  const handleDownload = async (event) => {
    event.stopPropagation();

    if (!document.documentKey && !document.fileId) {
      return;
    }

    if (isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      if (document.documentKey) {
        await downloadGeneratedDocumentFile({
          documentKey: document.documentKey,
          filename: document.filename,
        });
      } else if (document.fileId) {
        await fileUtils.saveFile(document.fileId, document.filename);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  if (isDownloading) {
    return (
      <Box
        sx={{
          width: "34px",
          height: "34px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <DefaultLoader size={20} />
      </Box>
    );
  }

  if (!document.documentKey && !document.fileId) {
    return (
      <Tooltip title={"File was downloaded to your computer"}>
        <span>
          <IconButton
            size={"small"}
            sx={{ color: "text.disabled", width: "34px", height: "34px" }}
            disabled
          >
            <DownloadIcon size={20} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={"Download"}>
      <IconButton
        onClick={handleDownload}
        size={"small"}
        sx={{ color: theme.brand.accent[600], width: "34px", height: "34px" }}
      >
        <DownloadIcon size={20} />
      </IconButton>
    </Tooltip>
  );
};

const GeneratedDocuments = ({ documents, submissionId, layerId, layer }) => {
  const theme = useTheme();
  if (!documents || documents.length === 0) {
    return null;
  }

  const formatCreatedAt = (createdAt, createdBy) => {
    const dateStr = readableDate(createdAt);

    if (createdBy) {
      return (
        <Tooltip title={`Created by ${createdBy}`}>
          <Typography variant={"body2"} sx={{ cursor: "help" }}>
            {dateStr}
          </Typography>
        </Tooltip>
      );
    }

    return <Typography variant={"body2"}>{dateStr}</Typography>;
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FAFAFA",
        border: `1px solid ${theme.brand.gray.gainsboro}`,
        borderRadius: "4px",
        padding: "12px 16px",
        marginTop: "4px",
        marginBottom: "8px",
      }}
    >
      <Table size={"small"} sx={{ "& td, & th": { border: 0 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ paddingLeft: 0 }}>
              <Typography variant={"subtitle2"} fontWeight={600}>
                {"Template"}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant={"subtitle2"} fontWeight={600}>
                {"Document Name"}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant={"subtitle2"} fontWeight={600}>
                {"Created"}
              </Typography>
            </TableCell>
            <TableCell align={"right"} sx={{ paddingRight: 0 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((document) => (
            <TableRow
              key={document.id}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                },
              }}
            >
              <TableCell sx={{ paddingLeft: 0 }}>
                <FileDetail
                  name={document.formattedFilename || document.templateName}
                  filename={document.override ? "(Override)" : ""}
                  type={document.type}
                />
              </TableCell>
              <TableCell>
                <Typography variant={"body2"}>{document.filename}</Typography>
              </TableCell>
              <TableCell>
                {formatCreatedAt(document.createdAt, document.createdBy)}
              </TableCell>
              <TableCell align={"right"} sx={{ paddingRight: 0 }}>
                <Stack
                  direction={"row"}
                  spacing={0.5}
                  justifyContent={"flex-end"}
                >
                  <DownloadButton document={document} />
                  <ActionMenu
                    document={document}
                    submissionId={submissionId}
                    layerId={layerId}
                    layer={layer}
                  />
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default GeneratedDocuments;
